import { Component, Input } from '@angular/core';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { DeviceService } from '../../device/device.service';
import { TranslateService } from '@ngx-translate/core';
import { SiteDashboardService } from '../../site-dashboard.service';
import * as moment from 'moment';
import { DeviceFactory } from '../../device/device-factory/device-factory';
import { CommonService } from 'src/app/shared/services/common.service';
import { DecimalPipe } from '@angular/common';

@Component({
	selector: 'app-monthly-quarterly-truck-usage-report',
	templateUrl: './monthly-quarterly-truck-usage-report.component.html',
	styleUrls: ['./monthly-quarterly-truck-usage-report.component.css']
})
export class MonthlyQuarterlyTruckUsageReportComponent {
	@Input() device: any = {};
	@Input() studyInfo: any = {}
	@Input() currentSite: any = {};
	// null leaves the existing behaviour untouched; only an explicit false shows "no data to show".
	@Input() isDeviceShipped: boolean | null = null;

	yearList: any[] = [];
	selectedYear: number = new Date().getFullYear();
	usageData = {};
	siteInstallationDate = null;

	periodTypes = [
		{label: this.translate.instant('g.monthly'), id: 'monthly'},
		{label: this.translate.instant('g.quarterly'), id: 'quarterly'},
	];

	selectedPeriodType = 'monthly';

	columnConfig: ColumnsConfig[] = [
		{key: 'period', name: this.translate.instant('connectivity_verification.month'), type: 'string'},
		{key: 'truck_usage_score', name: this.translate.instant('truck_performance.truck_usage_score'), type: 'number', hasTooltip: this.translate.instant('tooltip.monthly_usage_score_column')},
		{key: 'total_discharged_ah', name: this.translate.instant('truck_performance.total_discharged_ah'), type: 'number'},
		{key: 'truck_energy_score', name: this.translate.instant('truck_performance.truck_energy_score'), type: 'number', hasTooltip: this.translate.instant('tooltip.monthly_energy_usage_score_column')},
		{key: 'total_kwh_used', name: this.translate.instant('truck_performance.total_kwh_used'), type: 'number'},
		{key: 'total_in_use_hours', name: this.translate.instant('truck_performance.total_in_use_hours'), type: 'number'},
		{key: 'total_travel_hours', name: this.translate.instant('truck_performance.total_travel_hours'), type: 'number'},
		{key: 'total_lift_hours', name: this.translate.instant('truck_performance.total_lift_hours'), type: 'number'},
		{key: 'total_idle_hours', name: this.translate.instant('truck_performance.total_idle_hours'), type: 'number'},
		{key: 'total_off_charging_hours', name: this.translate.instant('truck_performance.total_off_charging_hours'), type: 'number'},
	];

	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: true,
		hasPagination: true,
		pageSize: 100,
		fitScreen: true,
		hideNoData: true,
		draggable: true,
		fileName: this.translate.instant('utilization.monthly_quarterly_truck_usage_report'),
		csvFileHeaderInfo: []
	};

	constructor(
		private deviceService: DeviceService,
		private translate: TranslateService,
		private siteDashboardService: SiteDashboardService,
		private commonService: CommonService,
	) {}

	ngOnInit() {
		if (this.device?.config_info?.installation_date)
			this.siteInstallationDate = moment.utc(this.device.config_info.installation_date * 1000).startOf('day').toDate();

		this.getMonthlyQuarterlyChargerUsageReport();
		const {yearList, currentYear} =  this.siteDashboardService.prepareYearsList(this.yearList, this.siteInstallationDate, this.studyInfo?.start_study_time);
		this.yearList = yearList;
		this.selectedYear = currentYear;
	}

	ngOnChanges(changes){
		if((changes.device || changes.studyInfo) && this.device && this.studyInfo)
			this.getMonthlyQuarterlyChargerUsageReport();
	}

	getMonthlyQuarterlyChargerUsageReport() {
		this.deviceService.getMonthlyQuarterlyChargerUsageReport(this.device.mac_address, this.selectedYear, DeviceFactory.types.iotah, 0, this.studyInfo.id).subscribe((res: any) => {
			if (!res.quarterly && !res.monthly)
				return;

			this.usageData = res;
			this.updateTableData(res?.[this.selectedPeriodType]?.[this.selectedYear] || []);
			this.tableConfig.csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
				customerName: this.currentSite.customer_name,
				siteName: this.currentSite.name,
				deviceSN: this.device.serial_number,
				includeSpacing: false
			})
		});
	}

	selectYear() {
		this.getMonthlyQuarterlyChargerUsageReport();
	}

	changePeriodType() {
		this.columnConfig[0].name = this.selectedPeriodType === 'monthly' ? this.translate.instant('connectivity_verification.month') : this.translate.instant('site.quarter');
		this.updateTableData(this.usageData?.[this.selectedPeriodType]?.[this.selectedYear] || []);
	}

	updateTableData(data) {
		const tableData = [];
		const decimalPipe: DecimalPipe = new DecimalPipe("en-US");

		for (const period in data) {
			if (data.hasOwnProperty(period)) {
				tableData.push({
					month: +data[period].month,
					period: {value: this.selectedPeriodType === 'monthly' ? this.commonService.monthsText[+data[period].month - 1] : data[period].quarter},
					truck_usage_score: {value: this.siteDashboardService.calculateUsageScore(data[period].daysCount, data[period].usedDaysCount)},
					total_discharged_ah: {value: Math.round((data[period].total_discharged_as || 0) / 3600).toLocaleString()},
					truck_energy_score: {value: Math.round(data[period].truck_energy_score || 0).toLocaleString()},
					total_kwh_used: {value: Math.round((data[period].total_ws_used || 0) / (3600 * 1000)).toLocaleString()},
					total_in_use_hours: {value: Math.round((data[period].total_in_use_second || 0) / 3600).toLocaleString()},
					total_travel_hours: {value: Math.round((data[period].total_travel_second || 0) / 3600).toLocaleString()},
					total_lift_hours: {value: Math.round((data[period].total_lift_second || 0) / 3600).toLocaleString()},
					total_idle_hours: {value: Math.round((data[period].total_idle_second || 0) / 3600).toLocaleString()},
					total_off_charging_hours: {value: Math.round((data[period].total_off_charging_second || 0) / 3600).toLocaleString()},
				});
			}
		}

		// sort by period
		tableData.sort((a, b) => { return a.month - b.month; });
		this.tableData = tableData;
	}
}
